import { Component, computed, input, output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { iconoirNavArrowLeft, iconoirNavArrowRight } from '@ng-icons/iconoir';
import { WiperLoadingComponent } from '../wiper-loading/wiper-loading.component';

interface CalendarDay {
  date: Date;
  day: number;
  isCurrentMonth: boolean;
  isToday: boolean;
  isMarked: boolean;
  isSelected: boolean;
}

@Component({
  selector: 'app-calendar',
  imports: [CommonModule, NgIcon, WiperLoadingComponent],
  providers: [provideIcons({ iconoirNavArrowLeft, iconoirNavArrowRight })],
  templateUrl: './calendar.component.html',
  styleUrl: './calendar.component.scss',
})
export class CalendarComponent {
  markedDates = input<Set<string>>(new Set());
  loading = input<boolean>(false);

  daySelected = output<string>();
  monthChanged = output<{ year: number; month: number }>();

  currentDate = signal(new Date());
  selectedDate = signal<string | null>(null);

  calendarWeeks = computed<CalendarDay[][]>(() => {
    const marked = this.markedDates();
    const selected = this.selectedDate();
    return this.buildCalendarWeeks(marked, selected);
  });

  weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  get currentMonthName(): string {
    return this.currentDate().toLocaleString('default', { month: 'long' });
  }

  get currentYear(): number {
    return this.currentDate().getFullYear();
  }

  navigateMonth(direction: number): void {
    const current = this.currentDate();
    const newDate = new Date(current.getFullYear(), current.getMonth() + direction, 1);
    this.currentDate.set(newDate);
    this.selectedDate.set(null);
    this.monthChanged.emit({ year: newDate.getFullYear(), month: newDate.getMonth() });
  }

  onDayClick(day: CalendarDay): void {
    if (!day.isCurrentMonth || !day.isMarked) return;
    const dateStr = this.formatDate(day.date);
    this.selectedDate.set(dateStr);
    this.daySelected.emit(dateStr);
  }

  private buildCalendarWeeks(marked: Set<string>, selected: string | null): CalendarDay[][] {
    const current = this.currentDate();
    const year = current.getFullYear();
    const month = current.getMonth();
    const today = new Date();
    const firstDayOfMonth = new Date(year, month, 1);
    const lastDayOfMonth = new Date(year, month + 1, 0);
    const startDay = firstDayOfMonth.getDay();

    const days: CalendarDay[] = [];

    for (let i = 0; i < startDay; i++) {
      const date = new Date(year, month, -(startDay - 1 - i));
      days.push({ date, day: date.getDate(), isCurrentMonth: false, isToday: false, isMarked: false, isSelected: false });
    }

    for (let d = 1; d <= lastDayOfMonth.getDate(); d++) {
      const date = new Date(year, month, d);
      const dateStr = this.formatDate(date);
      const isToday =
        date.getDate() === today.getDate() &&
        date.getMonth() === today.getMonth() &&
        date.getFullYear() === today.getFullYear();
      days.push({
        date, day: d, isCurrentMonth: true, isToday,
        isMarked: marked.has(dateStr),
        isSelected: selected === dateStr,
      });
    }

    const remaining = 7 - (days.length % 7);
    if (remaining < 7) {
      for (let i = 1; i <= remaining; i++) {
        const date = new Date(year, month + 1, i);
        days.push({ date, day: date.getDate(), isCurrentMonth: false, isToday: false, isMarked: false, isSelected: false });
      }
    }

    const weeks: CalendarDay[][] = [];
    for (let i = 0; i < days.length; i += 7) {
      weeks.push(days.slice(i, i + 7));
    }
    return weeks;
  }

  private formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
